import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  LayoutRectangle,
  Modal,
  Platform,
  TextInput,
  TextInputProps,
  TextProps,
  TouchableHighlightProps,
  TouchableOpacity,
  useWindowDimensions,
  View
} from 'react-native';
import { FlatList, TouchableHighlight } from 'react-native-gesture-handler';
import styled from 'styled-components/native';

import { Feather } from '@expo/vector-icons';
import Colors from '../../constants/Colors';
import ListItem from '../list/ListItem';
import ListItemText from '../list/ListItemText';
import { FormInputVariant } from './FormInput';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

const SelectView = styled.View`
  flex: 1;
`;

const LabelText = styled.Text<{ error?: boolean }>`
  font-family: 'DMSans-Bold';
  font-size: 16px;
  line-height: 20px;
  color: ${p => (p.error ? Colors.error : Colors.secondaryText)};
  margin-bottom: 2px;
`;

const StyledTouchableHighlight = styled.TouchableHighlight<{
  hasHelperText: boolean;
  variant?: FormInputVariant;
}>`
  position: relative;
  background-color: ${p =>
    p.variant === 'survey' ? Colors.primary : Colors.lightText};
  border: 1px solid ${Colors.formInputBorder};
  border-radius: ${p => (p.variant === 'survey' ? 26.5 : 8)}px;
  padding: ${p => (p.variant === 'chart' ? 10 : 16)}px;
  margin-bottom: ${p => (p.hasHelperText ? 4 : 12)}px;
` as React.FC<
  TouchableHighlightProps & {
    hasHelperText: boolean;
    ref: any;
    variant?: FormInputVariant;
  }
>;

const StyledText = styled.Text<{
  value?: string;
  variant?: FormInputVariant;
  empty?: boolean;
}>`
  color: ${p =>
    p.value
      ? p.variant === 'survey'
        ? Colors.lightText
        : Colors.darkText
      : Colors.placeholderColor};
  font-size: ${p => (p.variant === 'chart' ? 14 : 18)}px;
  padding-right: ${p => (p.variant === 'chart' ? 32 : 32)}px;
  padding-left: ${p => (p.variant === 'survey' ? 5 : 0)}px;
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Medium')};
  height: ${p => (p.variant === 'chart' ? 18 : 20)}px;
` as React.FC<
  TextProps & {
    value?: string;
    variant?: FormInputVariant;
    empty?: boolean;
  }
>;

const StyledDropDownIcon = styled.View<{ variant?: FormInputVariant }>`
  position: absolute;
  top: 0px;
  right: 0px;
`;

const StyledTextInput = styled.TextInput<{
  variant?: FormInputVariant;
  empty?: boolean;
}>`
  background-color: ${p =>
    p.variant === 'survey' ? Colors.primary : Colors.lightText};
  color: ${p => (p.variant === 'survey' ? Colors.lightText : Colors.darkText)};
  border: 1px solid ${Colors.formInputBorder};
  border-radius: ${p => (p.variant === 'survey' ? 26.5 : 8)}px;
  padding: ${p =>
    p.variant === 'survey'
      ? Platform.OS === 'android'
        ? '16px 16px 16px 22px'
        : '16px 16px 16px 22px'
      : p.variant === 'chart'
      ? '8px 16px 8px 11px'
      : '16px'};
  font-size: ${p => (p.variant ? 14 : 18)}px;
  height: ${p => (p.variant === 'chart' ? 39 : 54)}px;
  font-family: ${p => (p.empty ? 'WorkSans-Regular' : 'DMSans-Medium')};
  font-weight: normal;
` as React.FC<
  TextInputProps & {
    ref: any;
    variant?: FormInputVariant;
  }
>;

const HelperText = styled.Text`
  font-family: 'DMSans-Medium';
  font-size: 12px;
  line-height: 18px;
  color: ${Colors.formLabel};
  margin: 4px 4px 12px 4px;
`;

const StyledModalContainer = styled.TouchableOpacity`
  width: 100%;
  height: 100%;
`;

const StyledPosition = styled.View<{ rect?: LayoutRectangle }>`
  position: absolute;
  top: ${p => p.rect?.y || 0}px;
  left: ${p => p.rect?.x || 0}px;
  width: ${p => p.rect?.width || 0}px;
` as React.FC<{ rect?: LayoutRectangle }>;

const FormPickerMenu = styled.View<{ variant?: FormInputVariant }>`
  background-color: ${Colors.lightText};
  border-radius: ${p => (p.variant === 'survey' ? 8 : 0)}px;
  margin-top: ${p => (p.variant === 'survey' ? 12 : 0)}px;
  box-shadow: 0 8px 8px rgba(50, 50, 71, 0.08);
  elevation: 5;
  z-index: 1;
  max-height: 200px;
` as React.FC<{ variant?: FormInputVariant }>;

export interface SelectOption {
  value: string | number;
  label?: string;
}

export interface SelectProps {
  label?: string;
  value?: string | number;
  placeholder?: string;
  helperText?: string;
  variant?: FormInputVariant;
  autocomplete?: boolean;
  error?: boolean;
  options: string[] | SelectOption[];
  onChange?: (option: string | SelectOption) => void;
}

const SelectField: React.FC<SelectProps> = ({
  label,
  variant,
  helperText,
  placeholder,
  autocomplete,
  error,
  onChange,
  ...props
}) => {
  // const { scrollDeltaY } = useAppContext();
  const insets = useSafeAreaInsets();
  const scrollDeltaY = 0;
  const touchableHighlightRef = useRef<TouchableHighlight>(null!);
  const textInputRef = useRef<TextInput>(null!);
  const [openMenu, setOpenMenu] = useState(false);
  const [focused, setFocused] = useState(false);
  const value = props.value;
  const [layoutRect, setLayoutRect] = useState<LayoutRectangle>();
  const options = props.options.map((option: string | SelectOption) =>
    typeof option === 'string' || typeof option === 'number'
      ? { label: option, value: option }
      : option
  );

  const { width } = useWindowDimensions();

  useEffect(() => {
    setLayoutRect(layoutRect =>
      layoutRect
        ? {
            ...layoutRect,
            y: layoutRect.y - scrollDeltaY
          }
        : undefined
    );
  }, [scrollDeltaY]);

  useLayoutEffect(() => {
    setOpenMenu(false);
  }, [width]);
  return (
    <SelectView>
      {label && <LabelText error={Boolean(error)}>{label}</LabelText>}

      <StyledTouchableHighlight
        variant={variant}
        ref={(touchableHighlight: TouchableHighlight) => {
          touchableHighlightRef.current = touchableHighlight;
        }}
        underlayColor={Colors.primary + '11'} // add alpha hex
        onPress={() => {
          if (options.length <= 0) return;
          (touchableHighlightRef.current as any).measureInWindow(
            (x: number, y: number, width: number, height: number) => {
              y -= scrollDeltaY;
              setLayoutRect({
                x: variant === 'chart' && width < 200 ? x - 200 + width : x,
                y: y + (Platform.OS === 'android' ? insets.top : 0),
                width: variant === 'chart' && width < 200 ? 200 : width,
                height
              });
            }
          );
          setOpenMenu(true);
        }}
        hasHelperText={Boolean(helperText)}
      >
        <View>
          <StyledText
            empty={!options.find(o => o.value === value)?.label}
            value={options.find(o => o.value === value)?.label}
            numberOfLines={1}
            variant={variant}
          >
            {options.find(o => o.value === value)?.label || placeholder || ' '}
          </StyledText>
          <StyledDropDownIcon variant={variant}>
            {variant === 'survey' ? (
              <Feather name='chevron-down' size={40} color={Colors.lightText} />
            ) : (
              <Feather
                name='chevron-down'
                size={24}
                color={Colors.placeholderColor}
              />
            )}
          </StyledDropDownIcon>
        </View>
      </StyledTouchableHighlight>

      {helperText && <HelperText>{helperText}</HelperText>}
      <Modal
        supportedOrientations={['landscape', 'portrait']}
        transparent
        onRequestClose={() => setOpenMenu(false)}
        visible={openMenu}
      >
        <StyledModalContainer
          onPress={() => setOpenMenu(false)}
          activeOpacity={0}
        >
          <StyledPosition rect={layoutRect}>
            <TouchableOpacity onPress={() => setOpenMenu(false)}>
              <StyledTextInput
                empty={!options.find(o => o.value === value)?.label}
                variant={variant}
                editable={autocomplete || false}
                blurOnSubmit={false}
                ref={(textInput: TextInput) => {
                  textInputRef.current = textInput;
                }}
                placeholderTextColor={Colors.placeholderColor}
                placeholder={placeholder}
                {...props}
                value={
                  variant === 'chart'
                    ? options.find(o => o.value === value)?.label || undefined
                    : value
                    ? String(value)
                    : undefined
                }
                onChangeText={text => {
                  onChange?.(text);
                }}
                onFocus={() => {
                  setFocused(true);
                }}
                onBlur={event => {
                  setFocused(false);
                }}
                onSubmitEditing={() => {
                  setFocused(false);
                }}
                pointerEvents='none'
              />
            </TouchableOpacity>
            <FormPickerMenu variant={variant}>
              <FlatList
                data={options}
                {...props}
                keyExtractor={item => item.value.toString()}
                renderItem={({ item }) => (
                  <ListItem
                    onPress={() => {
                      setOpenMenu(false);
                      onChange?.(item);
                    }}
                  >
                    <ListItemText primary={item.label} {...props} />
                  </ListItem>
                )}
              />
            </FormPickerMenu>
          </StyledPosition>
        </StyledModalContainer>
      </Modal>
    </SelectView>
  );
};

export default SelectField;
